import { useMemo, useState } from 'react'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { calculateCoefficient } from '../../utils/calculator'
import ShapeFieldset from './components/ShapeFieldset'
import {
  EMPTY_SHAPE,
  describeShape,
  isShapeFilled,
  toHeight,
  toShape,
} from './shapeValue'
import './ShapeCalculatorPage.css'

/**
 * Пересчёт рецепта под другую форму: по объёму исходной и новой формы считает
 * коэффициент, на который нужно умножить ингредиенты.
 *
 * Отличается от калькулятора внутри рецепта тем, что не привязан к карточке:
 * размеры вводятся руками, поэтому инструмент работает с любым рецептом,
 * в том числе не из приложения.
 */
const ShapeCalculatorPage = () => {
  useTelegramBackButton(true)

  const [original, setOriginal] = useState(EMPTY_SHAPE)
  const [target, setTarget] = useState(EMPTY_SHAPE)
  const [weight, setWeight] = useState('')

  const isReady = isShapeFilled(original) && isShapeFilled(target)

  const coefficient = useMemo(() => {
    const originalShape = toShape(original)
    const targetShape = toShape(target)
    if (!originalShape || !targetShape) return 0
    return calculateCoefficient(
      originalShape,
      toHeight(original),
      targetShape,
      toHeight(target)
    )
  }, [original, target])

  const originalWeight = Number(weight) || 0

  const handleReset = () => {
    setOriginal(EMPTY_SHAPE)
    setTarget(EMPTY_SHAPE)
    setWeight('')
  }

  // safe-area-inset на корень не вешаем: шелл уже оборачивает роутинг в этот
  // класс, а повторный перебил бы наш padding-bottom нулём.
  return (
    <div className="shape-calc-page">
      <div className="shape-calc-body">
        <h1 className="shape-calc-title">Калькулятор формы</h1>
        <p className="shape-calc-subtitle">
          Укажите форму из рецепта и форму, в которой печёте, — посчитаем
          коэффициент, на который нужно умножить все ингредиенты.
        </p>

        <ShapeFieldset
          title="Форма из рецепта"
          value={original}
          onChange={setOriginal}
        />

        <ShapeFieldset title="Ваша форма" value={target} onChange={setTarget} />

        <label className="shape-calc-weight">
          <span>Вес рецепта, г (необязательно)</span>
          <input
            type="number"
            inputMode="decimal"
            value={weight}
            onChange={e => setWeight(e.target.value)}
            placeholder="1200"
          />
        </label>

        {isReady ? (
          <div className="shape-calc-result">
            <div className="shape-calc-coefficient">
              Коэффициент <strong>×{coefficient.toFixed(2)}</strong>
            </div>

            <p className="shape-calc-result-text">
              Умножьте на него количество каждого ингредиента.
            </p>

            {originalWeight > 0 && (
              <div className="shape-calc-weight-result">
                Примерный вес:{' '}
                <strong>~{Math.round(originalWeight * coefficient)} г</strong>
                <span> (исходный: {originalWeight} г)</span>
              </div>
            )}

            <dl className="shape-calc-summary">
              <div>
                <dt>Из рецепта</dt>
                <dd>{describeShape(original)}</dd>
              </div>
              <div>
                <dt>Ваша</dt>
                <dd>{describeShape(target)}</dd>
              </div>
            </dl>

            <p className="shape-calc-hint">
              Для крема и ганаша добавьте +5–10% к рассчитанному количеству,
              особенно для прямоугольных форм.
            </p>

            <button
              type="button"
              className="shape-calc-reset"
              onClick={handleReset}
            >
              Очистить
            </button>
          </div>
        ) : (
          <p className="shape-calc-placeholder">
            Заполните размеры и высоту обеих форм — расчёт появится здесь.
          </p>
        )}
      </div>
    </div>
  )
}

export default ShapeCalculatorPage
